// VocabGameDemo.js - With topic prop for games
import React, { useState, useEffect } from 'react';
import { 
  ThemeProvider, 
  createTheme, 
  CssBaseline,
  Container, 
  Box, 
  Typography, 
  Button,
  AppBar,
  Toolbar,
  IconButton,
  LinearProgress,
  Paper,
  Grid,
  Fade,
  Grow
} from '@mui/material';
import {
  VolumeUp,
  VolumeOff,
  Help,
  CheckCircle
} from '@mui/icons-material';

// Import game components
import WordToImageGame from './games/WordToImageGame';
import ImageToWordGame from './games/ImageToWordGame';
import BuildWordGame from './games/BuildWordGame';
import AnimatedBackground from './animations/AnimatedBackground';
import Confetti from './animations/Confetti';

// Import the GameContext provider
import { GameProvider } from '../../context/GameContext';

import correctSound from "../../assets/correct.mp3";


// Create a sample theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#007bff',
    },
    secondary: {
      main: '#28a745',
    },
    error: {
      main: '#dc3545',
    },
    background: {
      default: '#f4f6f8',
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-5px)',
            boxShadow: '0 12px 20px rgba(0, 0, 0, 0.15)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 'bold',
        },
      },
    },
  },
});

// Sample data
const SAMPLE_DATA = {
  config: {
    pointsPerCorrectAnswer: 10,
    questionsPerGame: 10, // Total questions to show before completion
    wordRepetitionCount: 2
  },
  topics: [
    {
      id: 'animals',
      name: 'Animals',
      words: [
        {
          id: 'animal1',
          word: 'Dog',
          image: 'https://images.unsplash.com/photo-1517849845537-4d257902454a?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80'
        },
        {
          id: 'animal2',
          word: 'Cat',
          image: 'https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80'
        },
        {
          id: 'animal3',
          word: 'Elephant',
          image: 'https://images.unsplash.com/photo-1557050543-4d5f4e07ef46?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80'
        },
        {
          id: 'animal4',
          word: 'Lion',
          image: 'https://images.unsplash.com/photo-1546182990-dffeafbe841d?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80'
        }
      ]
    },
    {
      id: 'fruits',
      name: 'Fruits',
      words: [
        {
          id: 'fruit1',
          word: 'Apple',
          image: 'https://images.unsplash.com/photo-1568702846914-96b305d2aaeb?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80'
        },
        {
          id: 'fruit2',
          word: 'Banana',
          image: 'https://images.unsplash.com/photo-1528825871115-3581a5387919?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80'
        },
        {
          id: 'fruit3',
          word: 'Orange',
          image: 'https://images.unsplash.com/photo-1582979512210-99b6a53386f9?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80'
        },
        {
          id: 'fruit4',
          word: 'Strawberry',
          image: 'https://images.unsplash.com/photo-1464965911861-746a04b4bca6?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80'
        }
      ]
    }
  ]
};

// Demo component 
export default function VocabularyGameDemo() {
  const [screen, setScreen] = useState('welcome'); // welcome, topics, game, completion
  const [soundOn, setSoundOn] = useState(true);
  const [gameType, setGameType] = useState(0); // 0: word-to-image, 1: image-to-word, 2: build-word
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes
  const [currentWord, setCurrentWord] = useState(null);
  const [selectedTopicId, setSelectedTopicId] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackData, setFeedbackData] = useState({ isCorrect: false, correctAnswer: null });
  const [questionsAnswered, setQuestionsAnswered] = useState(0);
  const [gameSequence, setGameSequence] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [debugInfo, setDebugInfo] = useState({});
  const [showConfetti, setShowConfetti] = useState(false);
  const [currentTopic, setCurrentTopic] = useState(null);

  // Format time for display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  // Shuffle array helper function
  const shuffleArray = (array) => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  };

  // Generate game sequence
  const generateGameSequence = (topicId) => {
    const topic = SAMPLE_DATA.topics.find(t => t.id === topicId);
    if (!topic) return [];

    // Create a sequence of questions
    const sequence = [];
    const words = [...topic.words];
    
    // Get all words in the topic
    for (let i = 0; i < SAMPLE_DATA.config.questionsPerGame; i++) {
      const gameTypeIndex = Math.floor(Math.random() * 3); // Random game type
      sequence.push({
        word: words[i%words.length],
        gameTypeIndex: gameTypeIndex
      });
    }
    
    console.log("Generated sequence:", sequence);
    return shuffleArray(sequence);
  };

  // Start game with selected topic
  const startGame = (topicId) => {
    setSelectedTopicId(topicId);
    setScore(0);
    setTimeLeft(300);
    setQuestionsAnswered(0);
    setCurrentQuestionIndex(0);
    setScreen('game');
    
    // Find and set the current topic
    const topic = SAMPLE_DATA.topics.find(t => t.id === topicId);
    setCurrentTopic(topic);
    
    // Generate game sequence
    const sequence = generateGameSequence(topicId);
    setGameSequence(sequence);
    
    if (sequence.length > 0) {
      // Initialize with first question
      loadQuestion(sequence[0]);
    }
  };

  // Load a specific question
  const loadQuestion = (questionData) => {
    console.log("Loading question:", questionData);
    const { word, gameTypeIndex } = questionData;
    setCurrentWord(word);
    setGameType(gameTypeIndex);
    
    // Update debug info
    setDebugInfo({
      currentIndex: currentQuestionIndex,
      sequenceLength: gameSequence.length,
      currentWord: word.word,
      gameType: gameTypeIndex
    });
  };

  // Handle answer submission from game components
  const handleAnswerSubmit = (isCorrect, correctAnswer = null) => {
    setFeedbackData({ isCorrect, correctAnswer });
    setShowFeedback(true);
    
    if (isCorrect) {
      setScore(prev => prev + SAMPLE_DATA.config.pointsPerCorrectAnswer);
      
      // Play sound if enabled
      if (soundOn) {
        // const sound = new Audio('https://cdn.pixabay.com/download/audio/2021/08/04/audio_0625c1539c.mp3?filename=correct-choice-43861.mp3');
        const sound = new Audio(correctSound);
        sound.play().catch(e => console.log("Error playing sound:", e));
      }
    }
    
    setQuestionsAnswered(prev => prev + 1);
  };

  // Handle continuing to next question after feedback
  const handleContinue = () => {
    console.log("Continue clicked, moving to next question");
    setShowFeedback(false);
    
    const nextIndex = currentQuestionIndex + 1;
    console.log("Next index:", nextIndex, "of", gameSequence.length);
    
    // Check if we've reached the end of the sequence
    if (nextIndex >= gameSequence.length) {
      console.log("End of sequence reached, showing completion");
      setShowConfetti(true);
      setScreen('completion');
    } else {
      setCurrentQuestionIndex(nextIndex);
      loadQuestion(gameSequence[nextIndex]);
    }
  };

  // Timer countdown effect
  useEffect(() => {
    if (screen === 'game') {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            setScreen('completion');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      return () => clearInterval(timer);
    }
  }, [screen]);

  // Load next question when currentQuestionIndex changes
  useEffect(() => {
    if (screen === 'game' && gameSequence.length > 0 && currentQuestionIndex < gameSequence.length) {
      loadQuestion(gameSequence[currentQuestionIndex]);
    }
  }, [gameSequence, currentQuestionIndex, screen]);

  // Render welcome screen
  const renderWelcome = () => (
    <Box sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
      <Grow in={true} timeout={800}>
        <Typography 
          variant="h2" 
          sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}
        >
          Vocabulary Game
        </Typography>
      </Grow>
      
      <Fade in={true} style={{ transitionDelay: '300ms' }}>
        <Typography 
          variant="h5" 
          sx={{ mb: 4, color: 'text.secondary' }}
        >
          Learn and practice vocabulary in a fun way!
        </Typography>
      </Fade>
      
      <Fade in={true} style={{ transitionDelay: '600ms' }}>
        <Button 
          variant="contained" 
          size="large"
          onClick={() => setScreen('topics')}
          sx={{ px: 4, py: 1.5, fontSize: '1.2rem' }}
        >
          Start Playing
        </Button>
      </Fade>
    </Box>
  );

  // Render topic selection
  const renderTopics = () => (
    <Box sx={{ width: '100%', maxWidth: 500, p: 2 }}>
      <Fade in={true} timeout={800}>
        <Typography 
          variant="h3" 
          sx={{ mb: 4, textAlign: 'center', fontWeight: 'bold' }}
        >
          Choose a Topic
        </Typography>
      </Fade>
      
      <Grid container spacing={2}>
        {SAMPLE_DATA.topics.map((topic, index) => (
          <Grid item xs={12} key={topic.id}>
            <Fade
              in={true}
              timeout={500}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <Button
                variant="outlined"
                fullWidth
                sx={{
                  p: 2,
                  fontSize: '1.2rem',
                  justifyContent: 'flex-start',
                  borderWidth: 2,
                  borderRadius: 3,
                  '&:hover': {
                    borderColor: 'primary.main',
                    transform: 'translateY(-5px)',
                  },
                }}
                onClick={() => startGame(topic.id)}
              >
                {topic.name}
              </Button>
            </Fade>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  // Render feedback modal
  const renderFeedback = () => (
    <Fade in={showFeedback}>
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: feedbackData.isCorrect ? '#d4edda' : '#f8d7da',
          color: feedbackData.isCorrect ? '#155724' : '#721c24',
          borderTop: '4px solid',
          borderColor: feedbackData.isCorrect ? 'success.main' : 'error.main',
          p: 2,
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {feedbackData.isCorrect ? (
            <CheckCircle sx={{ mr: 2, color: 'success.main', fontSize: 30 }} />
          ) : (
            <Box sx={{ mr: 2, color: 'error.main', fontSize: 30 }}>✕</Box>
          )}
          
          <Typography variant="h6">
            {feedbackData.isCorrect ? 'Correct!' : 'Incorrect'}
            {!feedbackData.isCorrect && feedbackData.correctAnswer && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Correct answer: <strong>{feedbackData.correctAnswer}</strong>
              </Typography>
            )}
          </Typography>
        </Box>
        
        <Button 
          variant="contained" 
          color={feedbackData.isCorrect ? "success" : "error"}
          onClick={handleContinue}
        >
          Continue
        </Button>
      </Box>
    </Fade>
  );

  // Render completion screen
  const renderCompletion = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        p: 3,
        position: 'relative',
        height: '100%',
        width: '100%',
      }}
    >
      {showConfetti && <Confetti active={true} />}
      
      <Fade in={true}>
        <Box
          sx={{
            width: 80,
            height: 80,
            bgcolor: 'success.main',
            borderRadius: '50%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            mb: 3,
            boxShadow: '0 0 15px rgba(40, 167, 69, 0.5)',
          }}
        >
          <CheckCircle sx={{ fontSize: 50, color: 'white' }} />
        </Box>
      </Fade>
      
      <Grow in={true} timeout={800} style={{ transitionDelay: '300ms' }}>
        <Typography variant="h3" sx={{ mb: 2, fontWeight: 'bold' }}>
          Great job!
        </Typography>
      </Grow>
      
      <Fade in={true} style={{ transitionDelay: '600ms' }}>
        <Paper elevation={3} sx={{ mb: 4, p: 3, width: '100%', maxWidth: 350 }}>
          <Typography variant="h5" sx={{ mb: 2, color: 'primary.main' }}>
            Your Score: {score}
          </Typography>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body1" color="text.secondary">Time Left:</Typography>
            <Typography variant="body1" fontWeight="bold">{formatTime(timeLeft)}</Typography>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body1" color="text.secondary">Questions Answered:</Typography>
            <Typography variant="body1" fontWeight="bold">{questionsAnswered}</Typography>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body1" color="text.secondary">Correct Answers:</Typography>
            <Typography variant="body1" fontWeight="bold">{Math.floor(score / SAMPLE_DATA.config.pointsPerCorrectAnswer)}</Typography>
          </Box>
        </Paper>
      </Fade>
      
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button 
          variant="contained" 
          onClick={() => {
            setShowConfetti(false);
            setScreen('topics');
          }}
        >
          Choose New Topic
        </Button>
        
        <Button 
          variant="outlined" 
          onClick={() => {
            setShowConfetti(false);
            startGame(selectedTopicId);
          }}
        >
          Play Again
        </Button>
      </Box>
    </Box>
  );

  // Debug panel
  const renderDebugPanel = () => (
    <Paper
      sx={{
        position: 'fixed',
        bottom: 10,
        right: 10,
        padding: 2,
        width: 300,
        zIndex: 9999,
        opacity: 0.8,
      }}
    >
      <Typography variant="h6">Debug Info</Typography>
      <Box sx={{ fontSize: '0.75rem' }}>
        <pre>
          {JSON.stringify({
            screen,
            currentIndex: currentQuestionIndex,
            totalQuestions: gameSequence.length,
            questionsAnswered,
            currentWord: currentWord?.word,
            gameType,
            ...debugInfo
          }, null, 2)}
        </pre>
      </Box>
    </Paper>
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GameProvider initialData={SAMPLE_DATA}>
        <Container
          // maxWidth={false}
          disableGutters
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #f4f6f8 0%, #dfe9f3 100%)',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <AnimatedBackground count={15} />
          
          <Paper
            elevation={6}
            sx={{
              width: { xs: '100%', sm: '100%', md: '200%' },
              height: { xs: '100vh', sm: '100vh', md: '100vh' },
              borderRadius: { xs: 0, sm: '20px' },
              // overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              zIndex: 1,
            }}
          >
            {/* Header */}
            <AppBar position="static" color="transparent" elevation={0}>
              <Toolbar>
                <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      bgcolor: 'grey.200',
                      px: 2,
                      py: 1,
                      borderRadius: 5,
                      fontWeight: 'bold',
                      color: timeLeft <= 60 ? 'error.main' : 'text.secondary',
                    }}
                  >
                    <Typography variant="body1">{formatTime(timeLeft)}</Typography>
                  </Box>
                  
                  <Box
                    sx={{
                      bgcolor: 'grey.200',
                      px: 2,
                      py: 1,
                      borderRadius: 5,
                      fontWeight: 'bold',
                      color: 'success.main',
                    }}
                  >
                    <Typography variant="body1">{score}</Typography>
                  </Box>
                </Box>
                
                <IconButton
                  onClick={() => setSoundOn(!soundOn)}
                  sx={{ bgcolor: 'grey.200' }}
                >
                  {soundOn ? <VolumeUp /> : <VolumeOff />}
                </IconButton>
                
                <IconButton
                  onClick={() => {}}
                  sx={{ ml: 1, bgcolor: 'grey.200' }}
                >
                  <Help />
                </IconButton>
              </Toolbar>
            </AppBar>
            
            {/* Progress Bar */}
            <LinearProgress
              variant="determinate"
              value={screen === 'game' ? (currentQuestionIndex / Math.max(1, gameSequence.length)) * 100 : 0}
              sx={{
                height: 6,
                bgcolor: 'grey.300',
                '& .MuiLinearProgress-bar': {
                  background: 'linear-gradient(90deg, #007bff, #00c6ff)',
                }
              }}
            />
            
            {/* Main content area */}
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                p: 3,
                position: 'relative',
              }}
            >
              {screen === 'welcome' && renderWelcome()}
              {screen === 'topics' && renderTopics()}
              {screen === 'game' && (
                gameType === 0 ? (
                  <WordToImageGame 
                    word={currentWord} 
                    topic={currentTopic}
                    onAnswerSubmit={handleAnswerSubmit} 
                  />
                ) : gameType === 1 ? (
                  <ImageToWordGame 
                    word={currentWord} 
                    topic={currentTopic}
                    onAnswerSubmit={handleAnswerSubmit} 
                  />
                ) : (
                  <BuildWordGame 
                    word={currentWord} 
                    onAnswerSubmit={handleAnswerSubmit} 
                  />
                )
              )}
              {screen === 'completion' && renderCompletion()}
              {showFeedback && renderFeedback()}
            </Box>
          </Paper>
          
          {/* Debug panel - change to true for debugging */}
          {false && renderDebugPanel()}
        </Container>
      </GameProvider>
    </ThemeProvider>
  );
}